import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

import type { ToastProps } from '@/components/ui/toast/toast.component';

const TOAST_LIMIT = 2;
const TOAST_REMOVE_DELAY = 10000;

type ToasterToast = ToastProps & {
  id: string;
  variant: 'default' | 'error' | 'warning';
  title?: React.ReactNode;
  description?: React.ReactNode;
};

interface ToastState {
  toasts: ToasterToast[];
  toastTimeouts: Map<string, ReturnType<typeof setTimeout>>;
}

interface ToastActions {
  test: () => void;
  addToast: (toast: ToasterToast) => void;
  dismissToast: (id: string) => void;
  clearAllToasts: () => void;
}

const initialState: ToastState = {
  toasts: [],
  toastTimeouts: new Map<string, ReturnType<typeof setTimeout>>(),
};

export const useToastStore = create<ToastState & ToastActions>()(
  devtools((set) => ({
    ...initialState,
    test: () => set((state) => ({ ...state })),
    addToast: (toast) =>
      set((state) => {
        const timeout = setTimeout(() => {
          state.toastTimeouts.delete(toast.id);
          state.dismissToast(toast.id);
        }, TOAST_REMOVE_DELAY);
        state.toastTimeouts.set(toast.id, timeout);
        return {
          ...state,
          toasts: [toast, ...state.toasts].slice(0, TOAST_LIMIT),
        };
      }),
    dismissToast: (id) =>
      set((state) => {
        if (state.toastTimeouts.has(id)) {
          clearTimeout(state.toastTimeouts.get(id));
          state.toastTimeouts.delete(id);
        }

        return { ...state, toasts: state.toasts.filter((t) => t.id !== id) };
      }),
    clearAllToasts: () =>
      set((state) => {
        // Clear all active timeouts
        state.toastTimeouts.forEach((timeout) => clearTimeout(timeout));
        return {
          ...state,
          toasts: [],
          toastTimeouts: new Map(),
        };
      }),
  })),
);
